import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'
import { userManager } from '@/Core/Managers'

/** Лічильники бейджів меню поточного користувача (ADR 030, `SPEC-014` §3.1). */
export const COUNTERS_URL = '/services/counters'

/** Подія каналу користувача в Centrifugo: `{event: 'counters', counters: {activity: 3}}`. */
export const COUNTERS_EVENT = 'counters'

/** Ядровий клієнт тримає GET у кеші пів хвилини — лічильник мусить бути свіжим. */
const NO_CACHE = { isDisableResponseCache: true }

/** Хто зараз увійшов — лічильники належать акаунту. */
const currentUser = (): string => String(userManager.user?.['uuid'] ?? userManager.user?.id ?? '')

/** Лише цілі невід'ємні числа: решта в події — сміття, а не значення. */
const sanitize = (raw: unknown): Record<string, number> => {
    if (raw === null || typeof raw !== 'object') return {}

    const result: Record<string, number> = {}

    for (const [code, value] of Object.entries(raw as Record<string, unknown>)) {
        if (typeof value === 'number' && Number.isFinite(value)) result[code] = Math.max(0, Math.trunc(value))
    }

    return result
}

/**
 * Лічильники користувача для бейджів меню: `value('activity')` — скільки справ
 * прострочено зараз.
 *
 * Звідки значення:
 *  - `load()` — `GET /services/counters`: на старті оболонки й після кожного
 *    (пере)підписання каналу Centrifugo, бо за час розриву події могли
 *    загубитися;
 *  - `apply()` — подія `counters` з каналу користувача (`CentrifugoConnector`),
 *    яку бек шле після кожної зміни справи й після проходу воркера.
 *
 * `revision` росте на кожне `apply()` і `load()`, навіть якщо число не
 * змінилося: «Робочий стіл» (`SPEC-015` С-2) перечитує віджети на подію, а не
 * на зміну значення.
 *
 * ⚠️ Значення прив'язані до акаунта (`loadedFor`): після виходу й входу іншим
 * користувачем без перезавантаження сторінки бейдж попереднього не
 * показується навіть на мить.
 */
class CounterManager {
    counters: Record<string, number> = {}

    /** Скільки разів лічильники оновлювалися (подія чи запит). */
    revision = 0

    private loadedFor: string | null = null

    /** Коли останній раз прочитано з сервера (мс) — щоб не читати двічі поспіль. */
    private loadedAt = 0

    /** Запит у польоті — кілька викликів дають один запит. */
    private loading: Promise<void> | null = null

    constructor() {
        makeAutoObservable<this, 'loading' | 'loadedAt'>(this, { loading: false, loadedAt: false })
    }

    get isLoaded(): boolean {
        return this.loadedFor !== null && this.loadedFor === currentUser()
    }

    /** Значення лічильника; для іншого акаунта чи до першого читання — 0. */
    value(code: string): number {
        if (!this.isLoaded) return 0

        return this.counters[code] ?? 0
    }

    /** Прочитати з сервера. Паралельні виклики чекають той самий запит. */
    load(): Promise<void> {
        this.loading ??= this.fetch().finally(() => {
            this.loading = null
        })

        return this.loading
    }

    /**
     * Прочитати, якщо для цього акаунта ще не читали або читали давніше за
     * `maxAgeMs`. Перше підписання каналу приходить за секунду після старту
     * оболонки — другий запит поспіль коштував би ще секунду воркера.
     */
    loadIfStale(maxAgeMs: number): Promise<void> {
        if (this.loading) return this.loading
        if (this.isLoaded && Date.now() - this.loadedAt < maxAgeMs) return Promise.resolve()

        return this.load()
    }

    /** Подія `counters` з Centrifugo: свіжі значення для поточного користувача. */
    apply(partial: unknown): void {
        const user = currentUser()
        const values = sanitize(partial)

        this.counters = this.loadedFor === user ? { ...this.counters, ...values } : values
        this.loadedFor = user
        this.revision++
    }

    /** Вихід: бейджів попереднього акаунта не лишається. */
    reset(): void {
        this.counters = {}
        this.loadedFor = null
        this.loadedAt = 0
    }

    private async fetch(): Promise<void> {
        const user = currentUser()

        if (!user) return

        try {
            const response = (await restClient.get(COUNTERS_URL, {}, NO_CACHE)) as unknown

            runInAction(() => {
                // Поки запит летів, увійшов інший акаунт — відповідь не його
                if (currentUser() !== user) return

                this.counters = sanitize(response)
                this.loadedFor = user
                this.loadedAt = Date.now()
                this.revision++
            })
        } catch {
            // Бейдж — підказка, а не дані: помилка читання не варта тосту.
            // Наступна подія чи перепідписання принесуть значення
        }
    }
}

export const counterManager = new CounterManager()
